import { useEffect, useLayoutEffect, useRef } from "react";
import { STATUS, decidePositioning } from "./bookByPriceHelpers";

/**
 * Keeps the best-bid anchor row centered in the SuperDOM scroll container.
 *
 * Auto-centering is active whenever the pointer is outside the panel.
 * While the pointer is inside, the user can scroll freely; centering
 * resumes the moment the pointer leaves (if drift exceeds the dead-zone).
 *
 * SCROLL-EVENT TIMING RATIONALE
 * Browser scroll-anchoring adjusts scrollTop when DOM changes (desired for
 * visual stability during hover) and fires scroll events. Those events must
 * not be confused with user-initiated scrolls, but since we no longer track
 * detached state, we simply ignore the scroll event entirely.
 */
export function useSuperDomAutoCenter({
  contentRef,
  ladder,
  bestBid,
  bestAsk,
  symbol,
  depth,
  viewMode,
  status,
  freezeAutoCenter = false,
}) {
  const pointerInsideRef = useRef(false);

  const isPointerInside = () => {
    const cont = contentRef.current;
    return (
      freezeAutoCenter ||
      pointerInsideRef.current ||
      !!cont?.matches?.(":hover")
    );
  };

  const measure = () => {
    const cont = contentRef.current;
    if (!cont) return null;
    if (cont.scrollHeight <= cont.clientHeight) return null;

    const anchor = cont.querySelector('[data-bbp-anchor="true"]');
    if (!anchor) return null;

    const cRect = cont.getBoundingClientRect();
    const aRect = anchor.getBoundingClientRect();
    if (!aRect.height) return null;

    const thead = cont.querySelector("thead");
    const headerH = thead ? thead.getBoundingClientRect().height : 0;
    const clientHeight = cRect.height;
    const usableCenter = headerH + (clientHeight - headerH) / 2;
    const anchorViewportCenter = aRect.top + aRect.height / 2 - cRect.top;
    const delta = anchorViewportCenter - usableCenter;

    return {
      delta,
      anchorViewportCenter,
      headerH,
      clientHeight,
      rowH: aRect.height,
    };
  };

  const lastProgrammaticScrollAt = useRef(0);
  const rafRef = useRef(0);

  // Apply a scroll using an already-computed measurement (no extra reflow).
  const doScrollWith = (m) => {
    const cont = contentRef.current;
    if (!cont || !m || Math.abs(m.delta) < 0.5) return;

    lastProgrammaticScrollAt.current = Date.now();
    cont.scrollTop += m.delta;
  };

  const doScroll = () => doScrollWith(measure());

  // Register pointer listeners once.
  useLayoutEffect(() => {
    const cont = contentRef.current;
    if (!cont) return undefined;

    const onPointerEnter = () => {
      pointerInsideRef.current = true;
    };
    const onPointerLeave = () => {
      pointerInsideRef.current = false;
    };

    cont.addEventListener("pointerenter", onPointerEnter);
    cont.addEventListener("pointerleave", onPointerLeave);

    return () => {
      cont.removeEventListener("pointerenter", onPointerEnter);
      cont.removeEventListener("pointerleave", onPointerLeave);
    };
  }, []);

  // Recenter on symbol / depth / mode change.
  useLayoutEffect(() => {
    if (isPointerInside()) return;
    doScroll();
  }, [symbol, depth, viewMode]);

  // Per-tick decision after each ladder/bestBid/bestAsk update.
  //
  // The book ticks far faster than the screen refreshes. Measuring/scrolling
  // synchronously in the layout effect on every tick forces a read→write→read
  // reflow storm (a dominant render hotpath). Instead we coalesce all ticks
  // that land within one frame into a single rAF: measure once, scroll once.
  useLayoutEffect(() => {
    if (status !== STATUS.LIVE) return;
    if (rafRef.current) return; // a frame is already pending — coalesce into it

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (status !== STATUS.LIVE) return;

      const m = measure();
      if (!m) return;

      const action = decidePositioning({
        detached: false,
        pointerInside: isPointerInside(),
        delta: m.delta,
        anchorViewportCenter: m.anchorViewportCenter,
        headerH: m.headerH,
        clientHeight: m.clientHeight,
        rowH: m.rowH,
      });

      if (action === "recenter") doScrollWith(m);
    });
  }, [freezeAutoCenter, ladder, bestBid, bestAsk, status]);

  // Cancel any pending frame on unmount.
  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    },
    [],
  );
}
